import { AnimatePresence, motion } from 'motion/react';
import { ANALYSIS_MESSAGES, STEP_COPY } from '../constants';
import {
  ONBOARDING_ANALYSIS_SETTLE_MS,
  useOnboardingAnalysisSettle,
} from '../hooks/useOnboardingAnalysisSettle';
import AnimatedInsightNumber from './AnimatedInsightNumber';
import { highlightedNumberClassName } from './InsightPointer';
import InsightSummary from './InsightSummary';
import OnboardingAnalyzingState from './OnboardingAnalyzingState';
import OnboardingInsightPanelShell from './OnboardingInsightPanelShell';

interface SignalsMatchInsightProps {
  signalIds: string[];
  matchCount: number;
  isCountLoading: boolean;
  hasCountError: boolean;
  skipInitialAnimation?: boolean;
}

const springSoft = { type: 'spring' as const, stiffness: 320, damping: 26 };

export default function SignalsMatchInsight({
  signalIds,
  matchCount,
  isCountLoading,
  hasCountError,
  skipInitialAnimation = false,
}: SignalsMatchInsightProps) {
  const selectionKey = signalIds.join(',');
  const isSettled = useOnboardingAnalysisSettle(selectionKey, { skipInitialAnimation });

  const isAnalyzing = !isSettled || isCountLoading;
  const displayCount = hasCountError && !isCountLoading ? 0 : matchCount;

  if (signalIds.length === 0) {
    return null;
  }

  return (
    <OnboardingInsightPanelShell isAnalyzing={isAnalyzing}>
      <div className="w-full" role="status" aria-live="polite">
        <AnimatePresence mode="wait">
          {isAnalyzing ? (
            <OnboardingAnalyzingState
              key={`analyzing-${selectionKey}`}
              selectionKey={selectionKey}
              messages={ANALYSIS_MESSAGES.signals}
              settleMs={ONBOARDING_ANALYSIS_SETTLE_MS}
            />
          ) : (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={springSoft}
              className="w-full"
            >
              <InsightSummary
                ariaLabel="Signal analysis summary"
                heading={
                  <>
                    Found{' '}
                    <AnimatedInsightNumber
                      value={displayCount}
                      className={highlightedNumberClassName}
                    />{' '}
                    {STEP_COPY.signals.matchResultLabel}
                  </>
                }
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </OnboardingInsightPanelShell>
  );
}
